/**
 * Activity Tracker Utility
 * Tracks user activity and provides methods to check inactivity
 */

// Initialize with current time to avoid immediate inactivity
let lastActivityTime = Date.now();
let activityListeners = [];
let isInitialized = false;

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "mouseup",
  "click",
  "keydown",
  "keypress",
  "scroll",
  "touchstart",
  "touchmove",
  "touchend",
];

/**
 * Update last activity timestamp
 */
export const updateActivity = () => {
  lastActivityTime = Date.now();
  // Notify all listeners
  activityListeners.forEach((listener) => listener(lastActivityTime));
};

/**
 * Check if activity tracking is initialized
 */
export const isActivityTrackingInitialized = () => {
  return isInitialized;
};

/**
 * Get last activity timestamp
 */
export const getLastActivityTime = () => {
  return lastActivityTime;
};

/**
 * Check if user has been inactive for specified duration
 * @param {number} inactivityThresholdMs - Threshold in milliseconds
 * @returns {boolean} - True if user has been inactive for threshold duration
 */
export const isUserInactive = (inactivityThresholdMs) => {
  const timeSinceLastActivity = Date.now() - lastActivityTime;
  return timeSinceLastActivity >= inactivityThresholdMs;
};

/**
 * Get milliseconds since last activity
 * @returns {number} - Milliseconds since last activity
 */
export const getTimeSinceLastActivity = () => {
  return Date.now() - lastActivityTime;
};

/**
 * Initialize activity tracking
 * Sets up event listeners for user activity
 */
export const initializeActivityTracking = () => {
  if (isInitialized) {
    // Already initialized, just update activity
    updateActivity();
    return;
  }

  // Remove existing listeners if any
  cleanupActivityTracking();

  // Set initial activity timestamp
  updateActivity();

  // Add event listeners
  ACTIVITY_EVENTS.forEach((event) => {
    window.addEventListener(event, updateActivity, { passive: true });
  });

  // Update activity on page visibility change (user switches back to tab)
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      updateActivity();
    }
  });

  isInitialized = true;
};

/**
 * Cleanup activity tracking
 * Removes all event listeners
 */
export const cleanupActivityTracking = () => {
  ACTIVITY_EVENTS.forEach((event) => {
    window.removeEventListener(event, updateActivity);
  });
  isInitialized = false;
};

/**
 * Add activity listener
 * @param {Function} listener - Callback function that receives lastActivityTime
 * @returns {Function} - Unsubscribe function
 */
export const addActivityListener = (listener) => {
  activityListeners.push(listener);
  return () => {
    activityListeners = activityListeners.filter((l) => l !== listener);
  };
};

/**
 * Reset activity tracking (useful for testing or manual reset)
 */
export const resetActivityTracking = () => {
  lastActivityTime = Date.now();
};

/**
 * Get initialization status
 */
export const getInitializationStatus = () => {
  return isInitialized;
};

